<script setup>
/**
 * MobileFiltersSheet
 * Bottom-sheet modal for mobile/tablet filter UI.
 * Slides up from the bottom with dimmed backdrop, supports swipe-to-dismiss.
 */
import { toRef, watch, onBeforeUnmount } from 'vue'
import CatalogFilters from './CatalogFilters.vue'
import { useBackButtonClose } from '../../../core/composables/useBackButtonClose.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'navigate-not-in-assortment'])

function close() {
  emit('update:modelValue', false)
}

// Intercept back button to close sheet instead of navigating
const { neutralizeHistory } = useBackButtonClose(toRef(props, 'modelValue'), close)

function handleNavigateNotInAssortment() {
  // Neutralize the dialog history entry so history.back() won't fire
  // and race with the parent's router navigation
  neutralizeHistory()
  close()
  emit('navigate-not-in-assortment', true)
}

// Body scroll lock
watch(
  () => props.modelValue,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : ''
  }
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
})

// Swipe-to-dismiss
let touchStartY = 0
let touchStartTime = 0
let dragging = false
let sheetEl = null

function onTouchStart(e) {
  const touch = e.touches[0]
  sheetEl = e.currentTarget
  // Only engage if touch starts in top 80px (handle/header area)
  const rect = sheetEl.getBoundingClientRect()
  if (touch.clientY - rect.top > 80) return
  touchStartY = touch.clientY
  touchStartTime = Date.now()
  dragging = true
  sheetEl.style.transition = 'none'
}

function onTouchMove(e) {
  if (!dragging || !sheetEl) return
  const dy = e.touches[0].clientY - touchStartY
  if (dy > 0) {
    sheetEl.style.transform = `translateY(${dy}px)`
  }
}

function onTouchEnd(e) {
  if (!dragging || !sheetEl) return
  dragging = false
  const dy = e.changedTouches[0].clientY - touchStartY
  const dt = Date.now() - touchStartTime
  const velocity = dy / dt // px/ms

  sheetEl.style.transition = ''
  sheetEl.style.transform = ''

  if (dy > 100 || velocity > 0.5) {
    close()
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div v-if="modelValue" class="sheet-backdrop" @click.self="close">
        <div
          class="sheet-panel"
          @touchstart.passive="onTouchStart"
          @touchmove.passive="onTouchMove"
          @touchend.passive="onTouchEnd"
        >
          <!-- Drag handle -->
          <div class="sheet-handle-area">
            <div class="sheet-handle" />
          </div>

          <!-- Header -->
          <div class="sheet-header">
            <div class="sheet-title-row">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
              </svg>
              <span class="sheet-title">סינון</span>
            </div>
            <button class="sheet-close-btn" @click="close" aria-label="סגור">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="sheet-content">
            <CatalogFilters compact @navigate-not-in-assortment="handleNavigateNotInAssortment" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.sheet-panel {
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  background: var(--bg-primary);
  border-radius: 16px 16px 0 0;
  box-shadow: var(--shadow-xl, 0 -4px 24px rgba(0, 0, 0, 0.15));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sheet-handle-area {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
  cursor: grab;
}

.sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--color-gray-300, #cbd5e1);
}

.sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 12px;
  border-bottom: 1px solid var(--border-color);
}

.sheet-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
}

.sheet-title {
  font-size: var(--font-size-base, 14px);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-primary);
}

.sheet-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--border-radius-full, 50%);
  background: var(--bg-tertiary, #f1f5f9);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background 0.15s;
}

.sheet-close-btn:active {
  background: var(--bg-secondary, #f8fafc);
}

.sheet-content {
  padding: 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Transition */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;
}

.sheet-enter-active .sheet-panel,
.sheet-leave-active .sheet-panel {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-from .sheet-panel,
.sheet-leave-to .sheet-panel {
  transform: translateY(100%);
}

/* Hide on desktop */
@media (min-width: 1280px) {
  .sheet-backdrop {
    display: none;
  }
}
</style>
